import { useEffect, useState } from "react";

import type { IBannerGalleryItem } from "@/@types/gamification";

import {
  type BannerGallerySelection,
  toGallerySelection,
} from "../utils/banner-source";

export interface BannerSourcePreview {
  url: string;
  label: string;
}

interface UseBannerSlotArgs {

  initialUrl?: string;
  sourcePreview?: BannerSourcePreview;
  setFile: (file: File | undefined) => void;
  setSource: (selection: BannerGallerySelection | undefined) => void;
}

export interface BannerSlotState {
  preview: string | null;
  galleryLabel: string | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onGallerySelect: (item: IBannerGalleryItem) => void;
  onRemove: () => void;
}

const isBlobUrl = (url: string | null): url is string =>
  typeof url === "string" && url.startsWith("blob:");

export function useBannerSlot({
  initialUrl,
  sourcePreview,
  setFile,
  setSource,
}: UseBannerSlotArgs): BannerSlotState {
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    setLocalPreview(initialUrl ?? null);
  }, [initialUrl]);

  useEffect(() => {
    if (!isBlobUrl(localPreview)) return;
    return () => URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFile(file);
    setSource(undefined);
    setLocalPreview(URL.createObjectURL(file));
  };

  const onGallerySelect = (item: IBannerGalleryItem): void => {
    setSource(toGallerySelection(item));
    setFile(undefined);
    setLocalPreview(null);
  };

  const onRemove = (): void => {
    setFile(undefined);
    setSource(undefined);
    setLocalPreview(null);
  };

  return {
    preview: sourcePreview?.url ?? localPreview,
    galleryLabel: sourcePreview?.label ?? null,
    onFileChange,
    onGallerySelect,
    onRemove,
  };
}
